import type { Experiments } from '@typings/redux';
import type {
	AnyAssignment,
	Configurations,
	Mappings,
	Name,
} from 'types/experiments';
import { experiments as experimentMap } from '../../../settings';

const getVariantId = (
	experimentConfig: Configurations,
	experimentId: string,
	bucketName: AnyAssignment,
	overrideMap: { [key: string]: string } = {},
) => {
	for (const property in experimentConfig) {
		if (property === experimentId) {
			const variants = experimentConfig[property as keyof Mappings]?.variants;
			const variantName = overrideMap[experimentId] ?? bucketName;
			const variant = variants?.find((v) => v.variant_name === variantName);

			return variant?.variant_id;
		}
	}
};

const getExperimentVariantMap = (experiments: Experiments | undefined) => {
	if (!experiments || !experiments.assignments) {
		return {};
	}

	const overrideMap = Object.entries(experiments.gatekeeper ?? {}).reduce(
		(acc, [name, value]) => {
			const id = experimentMap[name as Name];

			if (id) {
				acc[`${id}`] = `${value}`;
			}

			return acc;
		},
		{} as { [key: string]: string },
	);

	const crossCategoryExperimentId =
		experimentMap?.crossCategoryMarketplaceSearch;
	if (
		crossCategoryExperimentId &&
		!(`${crossCategoryExperimentId}` in overrideMap)
	) {
		overrideMap[`${crossCategoryExperimentId}`] = 'variant3';
	}

	const assignmentEntries = Object.entries(experiments.assignments);
	const crossCategoryConfig = crossCategoryExperimentId
		? experiments.configurations?.[crossCategoryExperimentId as keyof Mappings]
		: undefined;
	const crossCategoryHasVariant3 =
		crossCategoryConfig?.variants?.some((v) => v.variant_name === 'variant3') ??
		false;
	if (
		crossCategoryExperimentId &&
		crossCategoryHasVariant3 &&
		!assignmentEntries.some(
			([experimentId]) => experimentId === `${crossCategoryExperimentId}`,
		)
	) {
		assignmentEntries.push([`${crossCategoryExperimentId}`, null]);
	}

	return assignmentEntries.reduce(
		(map, [experimentId, bucketName]) => {
			map[experimentId] =
				getVariantId(
					experiments.configurations,
					experimentId,
					bucketName,
					overrideMap,
				) ?? '';

			return map;
		},
		{} as { [key: string]: string },
	);
};

const generateExperimentHeader = (
	experiments: Experiments | undefined,
): { [key: string]: string } => {
	if (!experiments || !experiments.assignments) {
		return {};
	}

	const experimentVariantMap = getExperimentVariantMap(experiments);

	return {
		'TKWW-Flipper-IDs': Object.entries(experimentVariantMap)
			.map(([key, value]) => `${key}=${value}`)
			.join(','),
	};
};

/** @internal */
export { getVariantId };
export { getExperimentVariantMap, generateExperimentHeader };
